import { useCallback, useEffect, useRef, useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import { activeChildId } from '../../store/selectors'
import { useVoiceCapture } from '../../hooks/useVoiceCapture'
import { CaptureButton } from './CaptureButton'
import { Waveform } from './Waveform'
import { TranscriptDisplay } from './TranscriptDisplay'
import { ExtractionCard } from './ExtractionCard'
import { TextCapture } from './TextCapture'
import { QuickRating } from './QuickRating'

/**
 * The capture layer — the most-used control in the app.
 *
 * Everything here answers to one number. If logging what just happened takes
 * longer than about ten seconds it does not happen at all, and every other
 * thing Aurora does is built on captures that were actually taken. So:
 *
 *   1. Nothing waits on the network, ever. The capture is written locally the
 *      instant the parent lets go, and the outbox delivers it whenever it can.
 *   2. There is always a way to log. Voice, then typing, then a single tap.
 *      Each fallback is one control away, none of them is second class.
 *   3. It floats over every view, in the thumb zone, at every screen width.
 */

type Mode = 'idle' | 'recording' | 'text' | 'quick'

/** Under this a recording has no words in it and saving would be noise. */
const MIN_USEFUL_MS = 500

export function CaptureLayer() {
  const childId = useAuroraStore(activeChildId)
  const view = useAuroraStore((st: any) => st.ui.view)
  const blockRef = useAuroraStore((s: any) => s.capture.blockRef as string | undefined)
  const pendingId = useAuroraStore((s: any) => s.capture.pendingCaptureId as string | undefined)
  const requested = useAuroraStore((s: any) => s.capture.recording as boolean)

  const voice = useVoiceCapture()
  const [mode, setMode] = useState<Mode>('idle')
  const [locked, setLocked] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const committing = useRef(false)

  const flash = useCallback((text: string) => {
    setNotice(text)
    window.setTimeout(() => setNotice((n) => (n === text ? null : n)), 2600)
  }, [])

  const begin = useCallback(async () => {
    if (mode === 'recording') return
    setLocked(false)
    setNotice(null)
    setMode('recording')
    const ok = await voice.start()
    if (!ok) {
      // No microphone and no permission is not a dead end — it is a text field.
      // A tap that landed while permission was being decided must not leave the
      // button sitting in its locked state over a session that never started.
      setLocked(false)
      setMode('text')
      ;(auroraStore.getState() as any).cancelRecording()
    }
  }, [mode, voice])

  const commit = useCallback(async () => {
    if (committing.current) return
    committing.current = true
    try {
      const { transcript, durationMs } = await voice.stop()
      const store = auroraStore.getState() as any
      setLocked(false)
      setMode('idle')

      const words = transcript.trim()
      if (!words) {
        store.cancelRecording()
        flash(
          durationMs < MIN_USEFUL_MS
            ? 'Hold the button while you talk'
            : 'Did not catch that — try again or type it',
        )
        return
      }
      if (!childId) return

      // The capture exists from here. Nothing below this line can fail in a
      // way that loses it, and nothing above it waited on a server.
      store.logVoiceCapture({ childId, transcript: words, blockRef })
    } finally {
      committing.current = false
    }
  }, [voice, childId, blockRef, flash])

  const abandon = useCallback(() => {
    voice.cancel()
    ;(auroraStore.getState() as any).cancelRecording()
    setLocked(false)
    setMode('idle')
  }, [voice])

  // A block card can ask for capture ("Log it"), so the layer follows.
  useEffect(() => {
    if (requested && mode === 'idle') {
      if (voice.support === 'none') setMode('text')
      else void begin()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requested])

  // Escape always bails out. A recording stuck open is worse than a lost note.
  useEffect(() => {
    if (mode !== 'recording') return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') abandon()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mode, abandon])

  if (!childId) return null

  const recording = mode === 'recording'
  const noMic = voice.support === 'none'

  return (
    <>
      {pendingId && <ExtractionCard captureId={pendingId} />}

      {mode === 'text' && (
        <TextCapture
          childId={childId}
          blockId={blockRef}
          initial={voice.transcript}
          onClose={() => {
            setMode('idle')
            ;(auroraStore.getState() as any).cancelRecording()
          }}
        />
      )}

      {mode === 'quick' && (
        <div className="capture-scrim" onClick={() => setMode('idle')} role="presentation">
          <div
            className="capture-panel"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Log how it went"
          >
            <p className="capture-panel-title">How did it go?</p>
            <QuickRating
              childId={childId}
              blockRef={blockRef}
              onDone={() => {
                setMode('idle')
                flash('Logged')
              }}
            />
            <div className="capture-panel-actions">
              <button className="btn btn-quiet btn-sm" onClick={() => setMode('idle')}>
                Cancel
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setMode('text')}>
                Type it instead
              </button>
            </div>
          </div>
        </div>
      )}

      {recording && (
        <div
          className="capture-scrim"
          onClick={locked ? undefined : abandon}
          role="presentation"
        >
          <div className="capture-panel is-recording" onClick={(e) => e.stopPropagation()}>
            <Waveform getLevel={voice.getLevel} active={voice.recording} />

            <TranscriptDisplay
              transcript={voice.transcript}
              interim={voice.interim}
              listening={voice.recording}
              transcribing={voice.support === 'full'}
            />

            {voice.error && <p className="capture-error">{voice.error}</p>}

            <div className="capture-panel-actions">
              <button className="btn btn-quiet btn-sm" onClick={abandon}>
                Cancel
              </button>
              <span className="capture-cue">
                {locked ? 'Tap the button to save' : 'Release to save'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Today carries its own mic in the Tell Aurora field; a second floating
          one there would be the same door twice. The dock still appears the
          moment a recording is live, so nothing started from that field is
          orphaned. */}
      {(view !== 'today' || recording || !!notice) && (
      <div className="capture-dock">
        {notice && (
          <p className="capture-toast" role="status">
            {notice}
          </p>
        )}

        <CaptureButton
          recording={recording}
          locked={locked}
          disabled={noMic}
          getLevel={voice.getLevel}
          onStart={() => (noMic ? setMode('text') : void begin())}
          onCommit={commit}
          onLock={() => setLocked(true)}
        />

        {!recording && (
          <button
            type="button"
            className="capture-alt"
            aria-label="Other ways to log this"
            onClick={() => setMode('quick')}
          >
            <MoreIcon />
          </button>
        )}
      </div>
      )}
    </>
  )
}

const MoreIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <circle cx="5" cy="12" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="19" cy="12" r="1.6" />
  </svg>
)
